import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet";
import { useQuery } from "@tanstack/react-query";
import { patientPaymentHistoryQueryOptions } from "../../encounters/api/queries/payment";
import { Button } from "@workspace/web-ui/components/button";
import { useSendInvoiceMutation } from "../../encounters/api/mutations";
import { MedicalInfoSection } from "~/features/dashboard/components/patient-medical-info";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

export interface HealthCondition {
  name: string
  status?: string
  medication?: string
  hba1c?: string
  diagnosed?: string
}

export interface Allergy {
  name: string
  severity: "Severe" | "Moderate" | "Mild"
  causes: string[]
}

interface PaymentDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  typeId: string;
  type: string;
  deliveryStatus: string;
  program: string;
  status: string;
}

export const PaymentDetailsSheet = ({
  open,
  onOpenChange,
  patientId,
  typeId,
  type,
  deliveryStatus,
  program,
  status,
}: PaymentDetailsSheetProps) => {
  const { data, isPending } = useQuery(patientPaymentHistoryQueryOptions(patientId, typeId, type));
  const sendInvoiceMutation = useSendInvoiceMutation();
  
  // Local state to track delivery status for immediate UI updates
  const [localDeliveryStatus, setLocalDeliveryStatus] = useState(deliveryStatus);

  // Sync local state with prop when it changes
  useEffect(() => {
    setLocalDeliveryStatus(deliveryStatus);
  }, [deliveryStatus]);

  const handleSendInvoice = async () => {
    try {
      await sendInvoiceMutation.mutateAsync({ patient_id: patientId, type_id: typeId, type });
      // Update local status immediately after successful mutation
      setLocalDeliveryStatus("sent");
    } catch (e) {
      // handle error
    }
  };

  if (!open) return null;

  // const healthConditions: HealthCondition[] = [
  //   {
  //     name: "Diabetes",
  //     status: "Patient is diabetic",
  //     medication: "Insulin",
  //     hba1c: "6.5%",
  //   },
  //   {
  //     name: "Hypertension",
  //     diagnosed: "June 2020",
  //     status: "Being treated but not well managed",
  //   },
  // ]

  // const allergies: Allergy[] = [
  //   {
  //     name: "Penicillin",
  //     severity: "Severe",
  //     causes: ["Anaphylaxis", "rash"],
  //   },
  //   {
  //     name: "Penicillin",
  //     severity: "Moderate",
  //     causes: ["Anaphylaxis", "rash"],
  //   },
  //   {
  //     name: "Penicillin",
  //     severity: "Mild",
  //     causes: ["Anaphylaxis", "rash"],
  //   },
  // ]

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        title="Payment Invoice Details"
        side="right"
        className="flex flex-col gap-6 overflow-y-auto p-1 sm:max-w-[620px] w-full focus:outline-none"
      >
        <SheetHeader className="px-5 pt-5">
          <SheetTitle className="text-[#2D2F35] text-[20px] font-medium">Payment Invoice Details</SheetTitle>
        </SheetHeader>

        {isPending ? (
          // skeleton...
          <div className="px-4">
            <div className="animate-pulse flex flex-col gap-4">
              <div className="h-32 bg-gray-200 rounded w-full mb-2" />
              <div className="h-32 bg-gray-200 rounded w-full mb-1" />
              <div className="h-32 bg-gray-200 rounded w-full mb-1" />
            </div>
          </div>
      ) : data ? (
          <div>
            <div className="flex flex-col gap-4 px-4 pb-48">
            {/* Patient Info */}
            <div className="bg-[#F1F4F9] rounded-lg p-3">
              <div className="text-lg font-semibold mb-3 text-gray-800">Patient Information</div>

              <div className="flex items-center gap-2 text-gray-600 mb-6 text-[14px]">
                {/* <span className="font-medium">
                  {data.patient.email && data.patient.email.length > 22
                    ? `${data.patient.email.slice(0, 22)}...`
                    : data.patient.email}
                </span>
                <span>•</span> */}
                <span className="font-medium">
                  {data.payment_reference.program
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')} Program
                </span>
                <span>•</span>
                <span className="font-medium">
                  {(() => {
                    const today = new Date();
                    const birthDate = new Date(data.patient.date_of_birth);
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                      age--;
                    }
                    return age;
                  })()} years
                </span>
                <span>•</span>
                <span className="font-medium">{data.patient.gender}</span>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Full name</div>
                  <div className="font-medium text-gray-800">{data.patient.first_name} {data.patient.last_name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">State</div>
                  <div className="font-medium text-gray-800">{data.patient.state}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">City</div>
                  <div className="font-medium text-gray-800">{data.patient.city || "-"}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">ZIP CODE</div>
                  <div className="font-medium text-gray-800">{data.patient.delivery_address?.zip_code}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">DOB</div>
                  <div className="font-medium text-gray-800">
                    {new Date(data.patient.date_of_birth).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Pharmacy</div>
                  <div className="font-medium text-gray-800">
                  <span className="text-xs sm:text-base font-medium text-wrap" style={{ color: '#2D2F35' }}>
                      {data.patient?.weight_loss_pharmacy?.fulfillment_type === "recommended" ? "ProxyMedicine's Recommended" : data.patient?.weight_loss_pharmacy?.pharmacy?.name || "--"}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email Address</div>
                  <div className="font-medium text-gray-800">{data.patient.email}</div>
                </div>
              </div>
            </div>
            {/* Shipping Info */}
            <div className="bg-[#F1F4F9] rounded-lg p-4">
              <div className="font-medium mb-2">Shipping information</div>
              <div className="grid grid-cols-2 gap-[8px] text-sm">
                <div className="font-medium"><span className="text-[12px] font-normal">Address:</span> <br /> {data.patient.delivery_address?.street}</div>
                <div className="font-medium"><span className="text-[12px] font-normal">City:</span> <br /> {data.patient.delivery_address?.city || "-"}</div>
                <div className="font-medium"><span className="text-[12px] font-normal">State/ZIP:</span> <br /> {data.patient.delivery_address?.state} {data.patient.delivery_address?.zip_code}</div>
                <div className="font-medium"><span className="text-[12px] font-normal">Phone number:</span> <br /> {data.patient.delivery_address?.phone_number}</div>
              </div>
            </div>

            <MedicalInfoSection
              healthConditions={data.patient.health_conditions}
              allergies={data.patient.allergies}
              title="Patient Medical History"
              className="bg-gray-50 rounded-lg p-6"
            />

            {/* Medications */}
            <div className="bg-[#F1F4F9] rounded-lg p-4">
              <div className="font-medium mb-2">Weight Loss Medications</div>
              <div className="text-[12px] font-normal mb-2">Based on the above, I'll prescribe</div>
              <div className="flex flex-col gap-2">
                {data.type_data.medications?.map((med: any, idx: number) => (
                  <div key={med.medication_id || idx} className="border rounded-[12px] bg-white p-[12px] mb-2">
                    <div className="font-medium mb-1">{med.name}</div>
                    <div className="grid grid-cols-3 gap-[8px]">
                      <div className="text-xs font-medium"><span className="font-normal">Dosage:</span> <br /> {med.dosage}</div>
                      {program === "weight-loss" && (
                        <div className="text-xs font-medium"><span className="font-normal">Vitamin:</span> <br /> {med.vitamin?.name}</div>
                      )}
                      {program === "urgent-care" && (
                      <div className="text-xs font-medium"><span className="font-normal">Quantity:</span> <br /> {med.quantity}</div>
                      )}
                      <div className="text-xs font-medium"><span className="font-normal">Doctors Instruction:</span> <br /> {med.instructions}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>

            {/* Send Invoice */}
            <div className="bg-white border-t mt-10 p-4 fixed bottom-0 z-10">
              <div className="font-medium mb-2">Send Invoice to Pharmacy</div>

              <div className="flex flex-row items-center border bg-white justify-between p-2 rounded-[12px] gap-4 mb-2 ">
                <div className="text-sm">
                  Invoice Status <br />
                  {localDeliveryStatus === "sent" ? (
                    <span className="text-green-600">Invoice sent</span>
                  ) : (
                    <span className="text-gray-500">Not sent</span>
                  )}
                </div>

                <Button 
                  onClick={handleSendInvoice} 
                  disabled={sendInvoiceMutation.isPending || status !== "paid"}
                  className="bg-[#4272DD] rounded-[8px] min-w-[140px]"
                >
                  {sendInvoiceMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    localDeliveryStatus === "sent" ? "Resend Invoice" : "Send Invoice"
                  )}
                </Button>
              </div>

              <div className="text-xs text-gray-500">
                This will send an email to scripts@luxemedpharmacy.com with a PDF invoice attachment containing all payment and prescription details.
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6">No details found.</div>
        )}
      </SheetContent>
    </Sheet>
  );
};